import type { Category, HomepageSettings, MomentView } from '../types'

// 開發模式由 Vite proxy 轉到 localhost:8080
// 生產模式前端和 API 同源，直接打 /api
const BASE = '/api'

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) }
  })
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
  return res.json() as Promise<T>
}

export const api = {
  listMoments: (category?: string) =>
    request<{ data: MomentView[] }>(`/moments${category ? `?category=${category}` : ''}`),

  listCategories: () => request<{ data: Category[] }>('/categories'),

  getHomepageSettings: () => request<{ data: HomepageSettings }>('/settings/homepage'),

  // 公開端點：回報停留時間（fire-and-forget）
  reportDwell: (momentId: string, ms: number) => {
    const body = JSON.stringify({ ms })
    // 優先用 sendBeacon（即使頁面正在關閉也會送）
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const ok = navigator.sendBeacon(
        `${BASE}/moments/${momentId}/dwell`,
        new Blob([body], { type: 'application/json' })
      )
      if (ok) return
    }
    // fallback: 一般 fetch
    fetch(`${BASE}/moments/${momentId}/dwell`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true
    }).catch(() => {})
  },

  // 後台專用 — 呼叫端需提供 admin token
  admin: (token: string) => ({
    resolveYouTube: (url: string) =>
      request<{ youtubeId: string; title: string; thumbnail: string | null; startSeconds: number }>(
        '/admin/resolve-youtube',
        {
          method: 'POST',
          headers: { authorization: `Bearer ${token}` },
          body: JSON.stringify({ url })
        }
      ),
    createVideo: (payload: { youtubeId: string; originalTitle: string; year?: number }) =>
      request<{ id: string; reused: boolean }>('/admin/videos', {
        method: 'POST',
        headers: { authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      }),
    createMoment: (payload: Record<string, unknown>) =>
      request<{ id: string }>('/moments', {
        method: 'POST',
        headers: { authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      }),
    updateHomepageSettings: (payload: Partial<HomepageSettings>) =>
      request<{ ok: true }>('/settings/homepage', {
        method: 'PATCH',
        headers: { authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      }),
    createCategory: (payload: {
      slug: string
      name: string
      color?: string
      description?: string
      sortOrder?: number
    }) =>
      request<{ id: string }>('/categories', {
        method: 'POST',
        headers: { authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      }),
    // slug 一旦建立就鎖住，這裡只允許 name/color/description/sortOrder
    updateCategory: (id: string, payload: Partial<Pick<Category, 'name' | 'color' | 'description' | 'sortOrder'>>) =>
      request<{ ok: true }>(`/categories/${id}`, {
        method: 'PATCH',
        headers: { authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      }),
    deleteCategory: (id: string) =>
      request<{ ok: true }>(`/categories/${id}`, {
        method: 'DELETE',
        headers: { authorization: `Bearer ${token}` }
      })
  })
}
